import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import loadable from '@loadable/component';

import { Breakpoint, Tabs, useBreakpoint } from '@jsmdg/yoshi';
import { type Experience, type Location } from '../../../shared/types';
import { eventTypes } from '../../types';
import { trackMapClicked, trackTabSwitched } from './venues.tracking';
import { VenuesList } from './VenuesList';
import { VenuesMapPreview } from './VenuesMapPreview';

const LoadableVenuesModal = loadable(async () => import('./VenuesModal'));

const messages = defineMessages({
    mapView: {
        defaultMessage: 'Kartenansicht',
    },
    listView: {
        defaultMessage: 'Listenansicht',
    },
});

type VenuesProps = {
    readonly product: Experience;
};

const Venues = ({ product }: VenuesProps): JSX.Element => {
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const intl = useIntl();
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [activeVenue, setActiveVenue] = useState<Location | undefined>(undefined);
    const isMobile = !useBreakpoint(Breakpoint.SM);

    useEffect(() => {
        function openLocationModal(): void {
            setIsLocationModalOpen(true);
        }

        document.addEventListener(eventTypes.VENUES_MAP_CLICK, openLocationModal);

        return function cleanup() {
            document.removeEventListener(eventTypes.VENUES_MAP_CLICK, openLocationModal);
        };
    }, [product]);

    const handleMapClicked = (): void => {
        setActiveVenue(undefined);
        setIsLocationModalOpen(true);
        trackMapClicked();
    };

    const getTrackingLabel = (tab: { caption: string }): string => {
        if (tab.caption === intl.formatMessage(messages.mapView)) {
            return 'MapView';
        }

        if (tab.caption === intl.formatMessage(messages.listView)) {
            return 'ListView';
        }

        return tab.caption;
    };

    const handleTabClick = (index: number, tab: { caption: string }): void => {
        setActiveTabIndex(index);
        const label = getTrackingLabel(tab);
        trackTabSwitched(label, tab.caption);
    };

    const handleVenueClicked = (venueId: string): void => {
        const venue = product.locations.find(location => location.externalId === venueId);
        setActiveVenue(venue);
        setIsLocationModalOpen(true);
    };

    const handleOnModalClose = (): void => {
        setIsLocationModalOpen(false);
    };

    const map = (
        <VenuesMapPreview mapImageUrl={product.mapImageUrl} onMapClicked={handleMapClicked} />
    );
    const list = <VenuesList locations={product.locations} onVenueClicked={handleVenueClicked} />;

    return (
        <>
            <div className="mb-4x">
                {isMobile ? (
                    <Tabs
                        selectedIndex={activeTabIndex}
                        onClick={handleTabClick}
                        tabs={[
                            {
                                caption: intl.formatMessage(messages.mapView),
                                content: map,
                            },
                            {
                                caption: intl.formatMessage(messages.listView),
                                content: list,
                            },
                        ]}
                    />
                ) : (
                    <div className="d-flex justify-content-start">
                        <div className="w-50">{map}</div>
                        <div className="w-50">{list}</div>
                    </div>
                )}
            </div>
            {isLocationModalOpen && (
                <LoadableVenuesModal
                    onRequestClose={handleOnModalClose}
                    productName={product.title}
                    isOpen={isLocationModalOpen}
                    locations={product.locations}
                    activeVenue={activeVenue as Location}
                    onVenueClicked={handleVenueClicked}
                />
            )}
        </>
    );
};

export { Venues };
